/**
 * "Me" on a card-lookup element — the signed-in person's OWN record of the
 * lookup's card (keepr-api utils/lookupMe.js is the canonical module;
 * docs/SCHEMA.md § Card definition, `options.defaultToMe`).
 *
 * A Property log's `staff` element looks up the Staff card; a Staff item
 * carries a `user` element marked as the account link (`options.identity`);
 * so "the Staff record whose account is mine" is one record or none — and
 * the API answers it: GET /api/collections/{id}/me?card_id=<target>
 * (useMe's `cardId`). These are the pure questions around that answer.
 *
 * Pure: no React, no antd, no HTTP.
 */
import { cardChain, mergeChainElements } from './cards';
import { normalizeId } from './ids';
import { identityElementOf } from './userElements';

export const LOOKUP_DATA_TYPE = 'card-lookup';

/** Does this element ask for the signed-in person's record on a new item? */
export const wantsDefaultToMe = element => (
	element?.dataType === LOOKUP_DATA_TYPE && element?.options?.defaultToMe === true
);

/**
 * Can a record of `cardId` — or of a card under it — be anybody's at all?
 * True when the card's effective list (own, ancestors', element sets') or
 * ANY descendant's carries an account link: the target of a lookup is
 * usually the general card (Person) and the link lives on the specific one
 * (Staff). The API's write gate asks the same question
 * (400 default_to_me_unavailable); the editor and the input ask it here so
 * a lookup nobody can be linked through never costs a request.
 *
 * `cardsById` is the app-wide cache; a card missing from it answers false
 * (no chain to read), which only ever hides a button.
 */
export function lookupFamilyHasIdentity({ cardsById = {}, elementSetsById = null, cardId } = {}) {
	const target = normalizeId(cardId);
	if (!target || !cardsById) return false;
	const hasLink = id => !!identityElementOf(mergeChainElements(cardChain(cardsById, id, elementSetsById)));
	if (hasLink(target)) return true;
	return Object.keys(cardsById).some(id => (
		id !== target
		&& cardChain(cardsById, id).some(node => normalizeId(node?._id) === target)
		&& hasLink(id)
	));
}

/**
 * Where a lookup finds its records, from a form in `collectionId`: the
 * target card's OWN collection when another collection owns it (a published
 * global collection's catalog), this collection otherwise. InputCardLookup
 * draws the same line for its picker; keepr-api lookupMe.recordsCollectionFor
 * for quick add.
 */
export function lookupRecordsCollectionId(targetDef, collectionId) {
	const here = normalizeId(collectionId) || '';
	const owner = targetDef?.scope === 'collection' ? normalizeId(targetDef.collection_id) : '';
	return owner && owner !== here ? owner : here;
}

/** The ids a stored lookup value holds, as strings. */
function idsOf(value) {
	const raw = Array.isArray(value) ? value : (value ? [value] : []);
	return raw.map(normalizeId).filter(Boolean);
}

/** Does the value already hold `meId`? */
export function lookupHoldsMe(value, meId) {
	const id = normalizeId(meId);
	return !!id && idsOf(value).includes(id);
}

/**
 * The value after "Me": the record itself for a single lookup, the record
 * APPENDED (once) for a multi one — "Me" adds a person to a crew, it does
 * not replace the crew.
 */
export function withMe(value, meId, { allowMultiple = false } = {}) {
	const id = normalizeId(meId);
	if (!id) return value;
	if (!allowMultiple) return id;
	const current = idsOf(value);
	return current.includes(id) ? current : [...current, id];
}

/** Is a lookup value empty ('' / null / [])? */
export const isEmptyLookupValue = value => (
	value === undefined || value === null || value === '' || (Array.isArray(value) && !value.length)
);
